/**
 * Left sidebar with categories and popular articles
 */

import { Link, useLocation } from 'react-router-dom';
import {
  BookOpen,
  ShoppingBag,
  User,
  CreditCard,
  Edit3,
  Wrench,
  List,
  LucideIcon,
} from 'lucide-react';
import { HelpCategory } from '../../types';
import { PopularArticle } from '../../services/helpContent';

// Icon mapping for categories
const categoryIconMap: Record<string, LucideIcon> = {
  'book-open': BookOpen,
  'shopping-bag': ShoppingBag,
  'user': User,
  'credit-card': CreditCard,
  'edit-3': Edit3,
  'edit': Edit3,
  'wrench': Wrench,
  'list': List,
};

interface HelpCategorySidebarProps {
  categories: HelpCategory[];
  popularArticles: PopularArticle[];
  selectedCategory?: string | null;
  getCategoryName?: (categoryId: string) => string;
}

export function HelpCategorySidebar({
  categories,
  popularArticles,
  selectedCategory,
  getCategoryName,
}: HelpCategorySidebarProps): JSX.Element {
  const location = useLocation();
  const isAllCategories = location.pathname === '/help' && !selectedCategory;

  return (
    <aside className="w-full lg:w-64 flex-shrink-0">
      {/* Categories */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Categories
        </h2>
        <nav>
          <ul className="space-y-1">
            <li>
              <Link
                to="/help"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isAllCategories
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <List size={16} />
                All Categories
              </Link>
            </li>
            {categories.map(category => {
              const Icon = categoryIconMap[category.icon] || BookOpen;
              const isSelected = selectedCategory === category.id;

              return (
                <li key={category.id}>
                  <Link
                    to={`/help/category/${category.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isSelected
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon size={16} />
                    {category.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Popular Articles */}
      {popularArticles.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Popular Articles
          </h2>
          <ul className="space-y-3">
            {popularArticles.slice(0, 5).map(article => (
              <li key={article.id}>
                <Link
                  to={`/help/article/${article.slug}`}
                  className="block group"
                >
                  <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 line-clamp-2">
                    {article.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {getCategoryName?.(article.categoryId) || article.categoryId}
                    {article.viewCount > 0 && (
                      <span className="ml-2">
                        {article.viewCount.toLocaleString()} views
                      </span>
                    )}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

export default HelpCategorySidebar;
